import { useMemo } from 'react'
import { createPortal } from 'react-dom'
import type { ECharts } from 'echarts'
import type { ScreenerCandle, ScreenerOIDataPoint } from '@/types'
import { formatCompact, formatUtc } from './chartUtils'
import { calculateOIRuler, intervalSeconds } from './oiRuler'
import { useChartRuler, type RulerSeries } from './useChartRuler'
import { RulerPopup } from './RulerPopup'
import { RulerOverlay } from './RulerOverlay'
import { RulerExchangeSummary } from './RulerExchangeSummary'

export interface RulerExchangeSeries {
  id: string
  label: string
  color: string
  oiData: ScreenerOIDataPoint[]
  candles: ScreenerCandle[]
  currentPrice?: number | null
}

export interface OIRulerSeries extends RulerSeries, RulerExchangeSeries {}

interface ChartRulerProps {
  chart: ECharts | null
  series: OIRulerSeries[]
  resetKey: string
  interval: string
  candleInterval?: string
  /** Price charts can display candles with a different interval from OI samples. */
  barInterval?: string
  metric?: 'price' | 'oi'
  /** Price ruler OI comes from every selected exchange, independently of its price source. */
  rulerExchanges?: RulerExchangeSeries[]
}

function number(value: number | null, price = false, digits = 4): string {
  if (value == null || !Number.isFinite(value)) return 'N/A'
  return value.toLocaleString(undefined, { maximumFractionDigits: price ? 8 : digits })
}

function duration(seconds: number): string {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor(seconds % 86400 / 3600)
  const minutes = Math.floor(seconds % 3600 / 60)
  const remainder = Math.floor(seconds % 60)
  return [days && `${days}d`, hours && `${hours}h`, minutes && `${minutes}m`, remainder && `${remainder}s`]
    .filter(Boolean).join(' ') || '0s'
}

export function ChartRuler({ chart, series, resetKey, interval, candleInterval, barInterval = interval, metric = 'oi', rulerExchanges }: ChartRulerProps) {
  const { selection, measuring, geometry, clear } = useChartRuler({ chart, series, resetKey })
  const selectedSeries = series.find((entry) => entry.id === selection?.seriesId)
  const startTime = selection?.startTimestamp
  const endTime = selection?.endTimestamp
  const measurement = useMemo(() => metric === 'oi' && startTime != null && endTime != null && selectedSeries ? calculateOIRuler({
    oiData: selectedSeries.oiData,
    candles: selectedSeries.candles,
    startTime,
    endTime,
    interval,
    candleInterval,
    currentPrice: selectedSeries.currentPrice,
  }) : null, [metric, startTime, endTime, selectedSeries, interval, candleInterval])

  if (!selection || !geometry || !selectedSeries) return null
  // The selected area follows the gesture; OI sample details use the time range below.
  const reversed = selection.startTimestamp > selection.endTimestamp
  const delta = geometry.endValue - geometry.startValue
  const percent = geometry.startValue !== 0 ? delta / Math.abs(geometry.startValue) * 100 : null
  const positive = delta >= 0
  const color = positive ? '#2dd4bf' : '#fb7185'
  const sign = positive ? '+' : ''
  const percentage = percent != null && Number.isFinite(percent) ? `${sign}${percent.toFixed(2)}%` : 'N/A'
  const unit = metric === 'price' ? 'USDT' : 'coins'
  const summary = `${sign}${metric === 'price' ? number(delta, true) : formatCompact(delta, 3)} ${unit} (${percentage})`
  const durationSeconds = Math.abs(selection.endTimestamp - selection.startTimestamp)
  const detail = `${Math.round(durationSeconds / intervalSeconds(barInterval))} bars · ${duration(durationSeconds)}`
  const firstTime = Math.min(selection.startTimestamp, selection.endTimestamp)
  const lastTime = Math.max(selection.startTimestamp, selection.endTimestamp)

  return <>
    <RulerOverlay geometry={geometry} selection={selection} color={color} summary={summary} detail={detail} metric={metric} interval={interval} />
    {createPortal(
      <RulerPopup anchor={geometry.anchor} side={reversed ? 'right' : 'left'} color={color} label={selectedSeries.label} metric={metric} measuring={measuring} onClear={clear}>
        <div className="border-b border-white/10 px-3 py-3">
          <div className="mb-1 text-[10px] text-slate-400">Selected area</div>
          <div className="font-mono text-base font-semibold" style={{ color }}>
            {sign}{number(delta, metric === 'price')} {unit} <span className="text-xs">({percentage})</span>
          </div>
          <div className="mt-0.5 text-[10px] text-slate-400">{detail}</div>
          <div className="mt-1 text-[10px] text-slate-500">{formatUtc(firstTime * 1000, interval === '5s')} → {formatUtc(lastTime * 1000, interval === '5s')}</div>
        </div>
        {metric === 'price' ? <RulerExchangeSummary
          exchanges={rulerExchanges ?? series} startTime={firstTime} endTime={lastTime}
          interval={interval} candleInterval={candleInterval}
        /> : measurement ? <div className="space-y-2.5 px-3 py-3">
          <div>
            <div className="text-[10px] text-slate-400">OI change in this time range</div>
            <div className="font-mono font-semibold" style={{ color: measurement.deltaCoins >= 0 ? '#2dd4bf' : '#fb7185' }}>
              {measurement.deltaCoins >= 0 ? '+' : ''}{number(measurement.deltaCoins)} coins
              <span className="ml-1.5 text-xs">({measurement.deltaPercent == null ? 'N/A' : `${measurement.deltaCoins >= 0 ? '+' : ''}${number(measurement.deltaPercent)}%`})</span>
            </div>
          </div>
          <div className="text-[10px] text-slate-500">{measurement.sampleCount} OI samples · {formatUtc(measurement.start.timestamp * 1000, interval === '5s')} → {formatUtc(measurement.end.timestamp * 1000, interval === '5s')}</div>
          <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 [&>dd]:text-right [&>dd]:font-mono">
            <dt className="text-slate-400">OI at start → end (coins)</dt><dd title={`${number(measurement.start.value)} → ${number(measurement.end.value)} coins`}>{formatCompact(measurement.start.value, 3)} → {formatCompact(measurement.end.value, 3)}</dd>
            <dt className="text-slate-400">Added / removed (coins)</dt><dd><span className="text-teal-300">{number(measurement.addedCoins)}</span> / <span className="text-rose-300">{number(measurement.removedCoins)}</span></dd>
            <dt className="text-slate-400">Start price (USDT)</dt><dd>{number(measurement.startPrice, true)}</dd>
            <dt className="text-slate-300">Est. average price (USDT)</dt><dd className="text-slate-50">{number(measurement.averagePrice, true)}</dd>
            <dt className="text-slate-400">End price (USDT)</dt><dd>{number(measurement.endPrice, true)}</dd>
            <dt className="text-slate-400">Latest price (USDT)</dt><dd>{number(measurement.currentPrice, true)}</dd>
          </dl>
          <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 border-t border-white/10 pt-2 [&>dd]:text-right [&>dd]:font-mono">
            <dt className="text-slate-400">OI change × avg (USDT)</dt><dd>{number(measurement.costBasisUsdt, false, 2)}</dd>
            <dt className="text-slate-400">OI change × end price (USDT)</dt><dd>{number(measurement.endValueUsdt, false, 2)}</dd>
            <dt className="text-slate-300">OI change × latest price (USDT)</dt><dd className="text-slate-50">{number(measurement.currentValueUsdt, false, 2)}</dd>
          </dl>
          {(measurement.hasGaps || !measurement.priceCoverageComplete || measurement.staleSamples > 0) && <p className="text-[10px] leading-relaxed text-amber-300">
            {measurement.hasGaps ? 'OI gaps: additions, removals and average unavailable. ' : ''}
            {!measurement.priceCoverageComplete ? 'Missing aligned prices: some estimates unavailable. ' : ''}
            {measurement.staleSamples > 0 ? `${measurement.staleSamples} samples have older exchange timestamps.` : ''}
          </p>}
          <p className="text-[10px] leading-relaxed text-slate-500">USDT values use the net coin change from the start. Changes extending it add at the observed price; offsets reduce it at the running average. Crossing the starting OI resets the average. OI changes do not identify buy/sell direction or actual entries.</p>
        </div> : <p className="px-3 py-3 text-slate-400">No OI samples are available for this range.</p>}
      </RulerPopup>, document.body,
    )}
  </>
}
